import i18n from '@/locales/i18n'
import shuttleEpcService from '@/services/shuttleEpc.service'
import type { PaginationDataType, PaginationType } from '@/types/PaginationType'
import { EHandler } from '@/types/enum/HandlerEnum'
import { EPaginationOptions } from '@/types/enum/PaginationOptionsEnum'
import type { ShuttleEpcModel } from '@/types/models/ShuttleEpcModel'
import { getPageSize } from '@/utils/routeUtils'
import { ref, type Ref } from 'vue'
import type { Commit, Dispatch } from 'vuex'
import { EAlertActions } from '../alertStore/AlertStoreTypes'
import { EStoreModules } from '../storeType'
import {
    EShuttleEpcActions,
    EShuttleEpcMutations,
    type ShuttleEpcState
} from './ShuttleEpcStoreTypes'

const state: Ref<PaginationDataType<ShuttleEpcState>> = ref({
    data: {
        shuttleEpcs: []
    },
    current_page: 1,
    last_page: 0,
    total: 0,
    to: 0,
    from: 0,
    per_page: getPageSize() ?? EPaginationOptions.DEFAULT,
    isLoading: false
})

const getters = {
    getShuttleEpcs: () => state.value.data.shuttleEpcs,
    isLoading: () => state.value.isLoading
}

const actions = {
    async [EShuttleEpcActions.FETCH_SHUTTLE_EPCS](
        { commit }: { commit: Commit },
        params: Partial<PaginationType>
    ) {
        commit(EShuttleEpcMutations.SET_LOADING, true)
        return await shuttleEpcService
            .getShuttleEpcs(params)
            .then(resp => {
                commit(EShuttleEpcMutations.SET_SHUTTLE_EPCS, resp.data)
                commit(EShuttleEpcMutations.SET_TOTAL, resp)
                return resp.data
            })
            .finally(() => {
                commit(EShuttleEpcMutations.SET_LOADING, false)
            })
    },

    async [EShuttleEpcActions.CREATE_SHUTTLE_EPC](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        epc: string
    ) {
        commit(EShuttleEpcMutations.SET_LOADING, true)
        return await shuttleEpcService
            .createShuttleEpc({ epc })
            .then(resp => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: epc,
                    key: EShuttleEpcActions.CREATE_SHUTTLE_EPC
                })
                return resp.data
            })
            .finally(() => commit(EShuttleEpcMutations.SET_LOADING, false))
    },

    async [EShuttleEpcActions.CREATE_SHUTTLE_EPC_RANGE](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { start, end }: { start: string; end: string }
    ) {
        commit(EShuttleEpcMutations.SET_LOADING, true)
        return await shuttleEpcService
            .createShuttleEpcRange({ start, end })
            .then(resp => {
                dispatch(
                    `${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
                    {
                        action: EAlertActions.SUCCESS,
                        message: i18n.global.t(
                            `shuttleEpc.api.success.${EShuttleEpcActions.CREATE_SHUTTLE_EPC_RANGE}`,
                            { start, end, created: resp.data.created, skipped: resp.data.skipped }
                        )
                    },
                    { root: true }
                )
                return resp.data
            })
            .finally(() => commit(EShuttleEpcMutations.SET_LOADING, false))
    },

    async [EShuttleEpcActions.DELETE_SHUTTLE_EPC](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { id, epc }: { id: number; epc: string }
    ) {
        commit(EShuttleEpcMutations.SET_LOADING, true)
        return await shuttleEpcService
            .deleteShuttleEpc(id)
            .then(res => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: epc,
                    key: EShuttleEpcActions.DELETE_SHUTTLE_EPC
                })
                return res
            })
            .finally(() => commit(EShuttleEpcMutations.SET_LOADING, false))
    },

    [EHandler.HANDLE_SUCCESS](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.SUCCESS,
                message: i18n.global.t(`shuttleEpc.api.success.${key}`, { name })
            },
            { root: true }
        )
        return { name }
    },
    [EHandler.HANDLE_ERROR](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.ERROR,
                message: i18n.global.t(`shuttleEpc.api.error.${key}`, { name })
            },
            { root: true }
        )
        return { name }
    }
}

const mutations = {
    [EShuttleEpcMutations.SET_SHUTTLE_EPCS](_: ShuttleEpcState, shuttleEpcs: ShuttleEpcModel[]) {
        state.value.data.shuttleEpcs = shuttleEpcs
    },

    [EShuttleEpcMutations.SET_TOTAL](
        _: ShuttleEpcState,
        data: PaginationDataType<ShuttleEpcModel>
    ) {
        const meta = (data as any).meta ?? data
        state.value.current_page = meta.current_page
        state.value.total = meta.total
        state.value.last_page = meta.last_page
        state.value.to = meta.to
        state.value.from = meta.from
    },

    [EShuttleEpcMutations.SET_LOADING](_: ShuttleEpcState, isLoading: boolean) {
        state.value.isLoading = isLoading
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
